import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import BlackjackAreaController, {
  useBlackjackActiveTurnPlayerId,
  useBlackjackDealerFirstCardDown,
  useBlackjackDealerHand,
  useBlackjackIsDealerDone,
  useBlackjackPlayer,
  useBlackjackPlayers,
} from '../../../../classes/BlackjackAreaController';
import PlayerController, { usePlayerGamePoints } from '../../../../classes/PlayerController';
import { useBlackjackAreaController } from '../../../../classes/TownController';
import useTownController from '../../../../hooks/useTownController';
import {
  BlackjackPlayer,
  BlackjackResult,
  Card,
  CardSuit,
} from '../../../../types/CoveyTownSocket';
import BlackjackArea from '../BlackjackArea';

function ActionButton({
  name,
  disableButton,
  onClick,
}: {
  name: string;
  disableButton: boolean;
  onClick: () => void;
}): JSX.Element {
  if (disableButton) {
    return (
      <Button
        onClick={() => {
          onClick();
        }}
        disabled={true}>
        {name}
      </Button>
    );
  } else {
    return (
      <Button
        onClick={() => {
          onClick();
        }}>
        {name}
      </Button>
    );
  }
}

function PlayerTitle({
  playerTitle,
  isThisPlayer,
  isPlayerTurn,
}: {
  playerTitle: string;
  isThisPlayer: boolean;
  isPlayerTurn: boolean;
}): JSX.Element {
  let bgColor = undefined;
  if (isPlayerTurn) {
    bgColor = 'yellow';
  }
  if (isThisPlayer) {
    return (
      <>
        <Text fontWeight='bold' backgroundColor={bgColor}>
          <Text color='red'>
            {'(You)'} {playerTitle}
          </Text>
        </Text>
      </>
    );
  } else {
    return (
      <>
        <Text fontWeight='bold' backgroundColor={bgColor}>
          {playerTitle}
        </Text>
      </>
    );
  }
}

function sumHandText(hand: Card[]): string {
  let numberOfAce = 0;

  hand.forEach(card => {
    if (card.value === 11) {
      numberOfAce += 1;
    }
  });
  let totalHandValue = 0;

  hand.forEach(card => {
    totalHandValue += card.value;
  });

  while (numberOfAce > 0 && totalHandValue > 21) {
    totalHandValue -= 10;
    numberOfAce -= 1;
  }

  return '            Total: ' + totalHandValue;
}

function handText({ hand, firstFaceDown }: { hand: Card[]; firstFaceDown: boolean }): string {
  let text = '';
  hand.forEach(card => {
    if (firstFaceDown && card === hand[0]) {
      text += '█';
    } else {
      let suit = '';
      switch (card.suit as CardSuit) {
        case 'heart':
          suit = '♥';
          break;
        case 'diamond':
          suit = '♦';
          break;
        case 'clover':
          suit = '♠';
          break;
        case 'club':
          suit = '♣';
          break;
        default:
          suit = '';
      }
      text += suit;
      text += card.valueName;
    }
    text += '\n';
  });
  return text;
}

function ResultText({
  blackjackController,
  id,
  hasBlackjack,
}: {
  result: BlackjackResult | undefined;
  blackjackController: BlackjackAreaController;
  id: string;
  hasBlackjack: boolean;
}): JSX.Element {
  const blackjackPlayer = blackjackController.blackjackPlayers.find(
    eachPlayer => eachPlayer.id == id,
  );
  if (blackjackPlayer?.result) {
    if (blackjackPlayer.result === 'won') {
      let pointsBack: number;
      if (hasBlackjack) {
        pointsBack = 2.5 * blackjackPlayer.bet;
      } else {
        pointsBack = 2 * blackjackPlayer.bet;
      }
      return (
        <>
          <Text color='green'>{'(Won +' + pointsBack + ')'}</Text>
        </>
      );
    } else if (blackjackPlayer.result === 'tie') {
      return (
        <>
          <Text color='blue'>{'(Tie +' + blackjackPlayer.bet + ')'}</Text>
        </>
      );
    } else if (blackjackPlayer.result === 'lost') {
      return (
        <>
          <Text color='red'>{'(Lost)'}</Text>
        </>
      );
    } else if (blackjackPlayer.result === 'bust') {
      return (
        <>
          <Text color='red'>{'(Bust)'}</Text>
        </>
      );
    }
  }
  return <></>;
}

function DealerHand({
  blackjackController,
}: {
  blackjackController: BlackjackAreaController;
}): JSX.Element {
  const dealerHand = useBlackjackDealerHand(blackjackController);
  const firstFaceDown = useBlackjackDealerFirstCardDown(blackjackController);
  let sumText = '';
  if (!firstFaceDown) {
    sumText = sumHandText(dealerHand);
  }
  return (
    <>
      <Text fontWeight='bold'>Dealer:</Text>
      <Text>
        {handText({ hand: dealerHand, firstFaceDown: firstFaceDown })}
        {sumText}
      </Text>
    </>
  );
}

function PlayersHand({
  blackjackController,
}: {
  blackjackController: BlackjackAreaController;
}): JSX.Element {
  const players = useBlackjackPlayers(blackjackController);
  const townController = useTownController();
  const activeTurnPlayerId = useBlackjackActiveTurnPlayerId(blackjackController);
  const thisPlayerId = townController.ourPlayer.id;

  const ids = [] as string[];
  const playerTitleTexts = new Map<string, string>();
  const handTexts = new Map<string, string>();
  const sumHandTexts = new Map<string, string>();
  const hasBlackjacks = new Map<string, boolean>();
  const results = new Map() as Map<string, BlackjackResult | undefined>;

  players.forEach(eachPlayer => {
    ids.push(eachPlayer.id);
    let playerName = '';
    const player = townController.players.find(
      playerController => playerController.id === eachPlayer.id,
    );
    if (player) {
      playerName = player.userName;
    }
    const blackjackPlayer = blackjackController.blackjackPlayers.find(
      eachBlackjackPlayer => eachPlayer.id === eachBlackjackPlayer.id,
    );
    if (blackjackPlayer) {
      hasBlackjacks.set(eachPlayer.id, blackjackPlayer.hasBlackjack);
    }
    const playerTitle = playerName + ' (bet: ' + eachPlayer.bet + ')';
    playerTitleTexts.set(eachPlayer.id, playerTitle);
    const newHand = [] as Card[];
    eachPlayer.hand.forEach(eachCard => {
      newHand.push({ suit: eachCard.suit, value: eachCard.value, valueName: eachCard.valueName });
    });
    results.set(thisPlayerId, eachPlayer.result);
    handTexts.set(
      eachPlayer.id,
      handText({
        hand: newHand,
        firstFaceDown: false,
      }),
    );
    sumHandTexts.set(eachPlayer.id, sumHandText(eachPlayer.hand));
  });
  return (
    <>
      {ids.map(id => {
        const result = results.get(id);
        let backgroundColor = undefined;
        if (id === activeTurnPlayerId) {
          backgroundColor = 'yellow';
        }
        let handValText = sumHandTexts.get(id);
        const hasBlackjack = hasBlackjacks.get(id) as boolean;
        if (hasBlackjack) {
          handValText = 'Blackjack!';
        }
        let playerTitle = '';
        const title = playerTitleTexts.get(id);
        if (title) {
          playerTitle = title;
        }
        return (
          <>
            <PlayerTitle
              playerTitle={playerTitle}
              isThisPlayer={id === thisPlayerId}
              isPlayerTurn={activeTurnPlayerId === id}
            />
            <Text backgroundColor={backgroundColor}>
              {handTexts.get(id)} {handValText}
            </Text>
            <ResultText
              result={result}
              blackjackController={blackjackController}
              id={id}
              hasBlackjack={hasBlackjack}
            />
          </>
        );
      })}
    </>
  );
}

function givePoints(
  result: BlackjackResult,
  player: PlayerController,
  bet: number,
  hasBlackjack: boolean,
): void {
  if (result === 'won') {
    player.gamePoints += 2 * bet;
    if (hasBlackjack) {
      player.gamePoints += bet / 2;
    }
  } else if (result === 'tie') {
    player.gamePoints += bet;
  }
}

function PlayerPointsText({
  playerController,
}: {
  playerController: PlayerController;
}): JSX.Element {
  const playerPoints = usePlayerGamePoints(playerController);
  return <>{'(Current Points: ' + playerPoints + ')'}</>;
}

function EndGameText({
  blackjackAreaController,
}: {
  blackjackAreaController: BlackjackAreaController;
}): JSX.Element {
  if (blackjackAreaController.isDealerDone) {
    return (
      <>
        <Text fontSize={15} color='blue'>
          Game has ended. Game points will be awarded when you leave the game window. If you want to
          play again, please leave the area and rejoin.
        </Text>
      </>
    );
  } else {
    return <></>;
  }
}

export default function BlackjackGamePlayModal({
  isOpen,
  blackjackArea,
}: {
  isOpen: boolean;
  blackjackArea: BlackjackArea;
}): JSX.Element {
  const toast = useToast();
  const townController = useTownController();
  const thisPlayer = townController.ourPlayer;
  const blackjackAreaController = useBlackjackAreaController(blackjackArea.name);
  const blackjackPlayers = useBlackjackPlayers(blackjackAreaController);
  const thisBlackjackPlayer = useBlackjackPlayer(blackjackAreaController, thisPlayer.id);
  const playerPoints = usePlayerGamePoints(thisPlayer);
  const activeId = useBlackjackActiveTurnPlayerId(blackjackAreaController);
  const isDealerDone = useBlackjackIsDealerDone(blackjackAreaController);
  townController.pause();
  const closeModal = useCallback(() => {
    const blackjackPlayer = blackjackPlayers.find(eachPlayer => eachPlayer.id === thisPlayer.id);
    if (blackjackPlayer && blackjackPlayer.result) {
      givePoints(
        blackjackPlayer.result,
        thisPlayer,
        blackjackPlayer.bet,
        blackjackPlayer.hasBlackjack,
      );
      if (thisPlayer.gamePoints === 0) {
        toast({
          title: 'Looks like you ran out of game points. We are giving you 1 point back!',
          status: 'info',
        });
        thisPlayer.gamePoints += 1;
      }
    }
    const remainingPlayers = [] as BlackjackPlayer[];
    blackjackAreaController.blackjackPlayers = remainingPlayers;
    townController.unPause();
    if (blackjackAreaController.blackjackPlayers.length === 0) {
      townController.interactEnd(blackjackArea);
      blackjackAreaController.reset();
      townController.emitBlackjackAreaUpdate(blackjackAreaController);
    }
  }, [blackjackPlayers, blackjackAreaController, townController, thisPlayer, toast, blackjackArea]);
  const [disableButtons, setDisableButtons] = useState(activeId !== thisPlayer.id || isDealerDone);
  useEffect(() => {
    const updater = (activePlayerId: string) => {
      if (activePlayerId === thisPlayer.id) {
        setDisableButtons(isDealerDone);
      } else {
        setDisableButtons(true);
      }
    };
    const isDealerDoneuUdater = (newIsDealerDone: boolean) => {
      setDisableButtons(disableButtons || newIsDealerDone);
    };
    blackjackAreaController.addListener('activeTurnPlayerChange', updater);
    blackjackAreaController.addListener('isDealerDoneChange', isDealerDoneuUdater);
    return () => {
      blackjackAreaController.removeListener('activeTurnPlayerChange', updater);
      blackjackAreaController.removeListener('isDealerDoneChange', isDealerDoneuUdater);
    };
  });

  const [disableHit, setDisableHit] = useState(false);
  const [disableDoubleDown, setDisableDoubleDown] = useState(false);

  const onClickHit = useCallback(async () => {
    townController.emitBlackjackPlayerAction({
      areaId: blackjackAreaController.id,
      playerId: thisPlayer.id,
      blackjackAction: 'hit',
    });
    setDisableDoubleDown(true);
  }, [blackjackAreaController.id, thisPlayer.id, townController]);

  const onClickStand = useCallback(async () => {
    townController.emitBlackjackPlayerAction({
      areaId: blackjackAreaController.id,
      playerId: thisPlayer.id,
      blackjackAction: 'stand',
    });
  }, [blackjackAreaController.id, thisPlayer.id, townController]);

  const onClickDoubleDown = useCallback(async () => {
    if (thisBlackjackPlayer && playerPoints >= thisBlackjackPlayer?.bet) {
      townController.emitBlackjackPlayerAction({
        areaId: blackjackAreaController.id,
        playerId: thisPlayer.id,
        blackjackAction: 'double down',
      });
      setDisableHit(true);
      setDisableDoubleDown(true);
      thisPlayer.gamePoints -= thisBlackjackPlayer.bet;
      thisBlackjackPlayer.bet *= 2;
    } else {
      toast({ title: 'Not enough points to bet', status: 'error' });
    }
  }, [
    blackjackAreaController.id,
    playerPoints,
    thisBlackjackPlayer,
    thisPlayer,
    toast,
    townController,
  ]);
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (isDealerDone) {
          closeModal();
        } else toast({ title: 'Cannot exit while in an active game', status: 'error' });
      }}
      closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Blackjack <PlayerPointsText playerController={thisPlayer} />
          <EndGameText blackjackAreaController={blackjackAreaController} />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <DealerHand blackjackController={blackjackAreaController} />
          <PlayersHand blackjackController={blackjackAreaController} />
        </ModalBody>
        <ModalFooter>
          <ActionButton
            name={'Hit'}
            disableButton={disableHit || disableButtons}
            onClick={onClickHit}
          />
          <ActionButton
            name={'Double Down'}
            disableButton={disableDoubleDown || disableButtons}
            onClick={onClickDoubleDown}
          />
          <ActionButton name={'Stand'} disableButton={disableButtons} onClick={onClickStand} />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
